/**
 * Geocodificador especializado para organizaciones humanitarias andaluzas
 *
 * Conecta con DERA G12 (Servicios) para acceder a la capa:
 * - `g12_36_OrganizacionesHumanitarias` — 107 features Andalucía
 *   (verif. empírica 2026-05-19, dataset completo n=107, sin truncamiento).
 *   Distribución por tipo de organización (n=107):
 *   Cruz roja=97 (90,7 %) · Cáritas=10 (9,3 %).
 *
 * Contexto institucional:
 * - Decreto 197/2024 (PTEL · Sistema Local de Protección Civil)
 * - Organizaciones del Sistema Local de Protección Civil (Anexo I)
 *
 * Fuente oficial:
 * - WFS DERA G12: https://www.ideandalucia.es/services/DERA_g12_servicios/wfs
 * - Layer:        DERA_g12_servicios:g12_36_OrganizacionesHumanitarias
 *
 * Schema empírico (sample completo n=107 GetFeature application/json,
 * 2026-05-19, persistido en
 * `.ptel/p0_wave2_20260519/sample_g12_36_n500.json`):
 *
 * | Campo       | Tipo (sample)  | Nillable | Cardinalidad |
 * |:------------|:---------------|:---------|:-------------|
 * | id_dera     | number         | NO       | 1..1         |
 * | nombre      | string         | YES      | 0..1         |
 * | tipo        | string         | YES      | 0..1 (2 vals: Cruz roja\|Cáritas) |
 * | direccion   | string         | YES      | 0..1 (texto libre con dirección postal) |
 * | cod_mun     | string         | YES      | 0..1 (INE5)  |
 * | municipio   | string         | YES      | 0..1         |
 * | provincia   | string         | YES      | 0..1         |
 * | geom        | MultiPoint     | YES      | 0..1 (card.1)|
 *
 * NOTA HX-CAPTURA-MAXIMAL (rumbo 2 · 3.ª aplicación productiva 2026-05-19):
 * - `parseFeature` preserva la totalidad del schema fuente en `properties`,
 *   no un denominador común. Esto garantiza que la BBDD enriquecida derivada
 *   del pipeline conserve los campos específicos de g12_36 (`nombre`,
 *   `tipo`, `direccion`, `id_dera`) aunque el flujo activo de geocoding
 *   no los necesite todos.
 * - A diferencia de g12_38 maremotos y g12_37 puestos socorro, g12_36
 *   SÍ expone `direccion` (dirección postal completa) → se propaga al
 *   campo `address` del `WFSFeature` resultante para uso directo en UI.
 * - Se añaden 4 campos de provenance (`source_layer`, `source_endpoint`,
 *   `schema_version`, `fetched_at`).
 *
 * Tipología (DOP-WAVE2-7=α, ratificada empíricamente 2026-05-19): se usa
 * la macro `TipologiaPTEL.EMERGENCIAS` por coherencia cluster F2 SEG
 * ampliado oleada-2 (alineada con WAVE-1 maremotos g12_38 y WAVE-2
 * puestos socorro g12_37) y mapeo canónico `TIPOLOGIA_KEYWORDS` del
 * proyecto (`cruz roja` ∈ EMERGENCIAS, `toProcessedRecord.ts` L46
 * → cubre 97/107 = 90,7 % features). El detalle fino
 * (`ORGANIZACION_HUMANITARIA` + `tipo` Cruz roja\|Cáritas) se persiste
 * como atributo libre (TUN1: «No Decreto fixes closed taxonomy»).
 *
 * @module services/geocoding/specialized
 * @since  2026-05-19 (WAVE-2 humanitarias · rumbo 2)
 */

import type {
  WFSFeature,
  WFSSearchOptions,
} from './WFSBaseGeocoder';
import { WFSBaseGeocoder } from './WFSBaseGeocoder';
import type { GeoJSONFeature } from '../../../types/geojson';
import { getStringProp } from '../../../types/geojson';
import type { SpecializedGeocoderConfig } from '../../../types/infrastructure';

/**
 * Opciones de búsqueda específicas para organizaciones humanitarias.
 *
 * Extiende `WFSSearchOptions` con `tipoOrganizacion` para filtrar por
 * categoría de organización (campo `tipo` de la fuente: «Cruz roja» o
 * «Cáritas» — verif. empírica n=107, sin terceros valores en el corpus).
 */
export interface OrganizacionesHumanitariasSearchOptions
  extends WFSSearchOptions {
  /** Tipo de organización (`tipo`); NULL-permisivo en la fuente. */
  tipoOrganizacion?: string;
}

/**
 * Identificador semántico granular del dataset (no es una `TipologiaPTEL`).
 *
 * Se persiste en `properties.tipoOriginal` para que la BBDD enriquecida
 * preserve la naturaleza específica del punto sin contaminar la taxonomía
 * macro cerrada de 19 categorías (TUN1).
 */
export const TIPO_ORIGINAL_ORGANIZACION_HUMANITARIA =
  'ORGANIZACION_HUMANITARIA' as const;

/**
 * Versión del schema empírico verificada. Incrementar si el
 * `DescribeFeatureType` upstream cambia (nuevos campos, nuevas
 * cardinalidades, cambio de tipo).
 */
export const SCHEMA_VERSION_G12_36 = 'g12_36_v20260519' as const;

/**
 * Geocodificador especializado para organizaciones humanitarias andaluzas.
 *
 * Precisión esperada: ±5-10 m (coordenadas oficiales DERA G12 publicadas
 * en EPSG:25830 nativo, sin reproyección intermedia).
 *
 * Cobertura: 107 features (dataset completo) distribuidos por las 8
 * provincias andaluzas. 97 sedes/asambleas Cruz Roja + 10 sedes Cáritas.
 *
 * Independiente de `WFSPuestoSocorroGeocoder` y `WFSMaremotosGeocoder`
 * (cluster F2 SEG ampliado oleada-2) por decisión arquitectónica
 * `DOP-WAVE1-1` 18-may: cada capa DERA G12 tiene su propio geocoder
 * dedicado para preservar HX-CAPTURA-MAXIMAL pleno (schemas
 * heterogéneos cross-capa: 5 campos comunes + 2 específicos g12_36
 * `tipo`+`direccion` perderían bajo anti-patrón denominador común).
 *
 * @example
 * ```typescript
 * const geocoder = new WFSOrganizacionesHumanitariasGeocoder();
 * const result = await geocoder.geocode({
 *   name: 'Cruz Roja Asamblea Local Baena',
 *   municipality: 'Baena',
 *   codMun: '14007',
 *   tipoOrganizacion: 'Cruz roja',
 * });
 * // result.confidence >= 80 (match oficial DERA)
 * // result.address === 'C/ Amador de los Ríos, 44. 14850, Baena, Córdoba'
 * // result.properties.tipoOriginal === 'ORGANIZACION_HUMANITARIA'
 * // result.properties.tipologiaPTEL === 'EMERGENCIAS'
 * // result.properties.contextoEmergencia === 'HUMANITARIA_GENERAL'
 * // result.properties.tipo === 'Cruz roja'
 * ```
 */
export class WFSOrganizacionesHumanitariasGeocoder extends WFSBaseGeocoder {
  /** Endpoint unificado DERA G12 Servicios. */
  public static readonly DERA_G12_ENDPOINT =
    'https://www.ideandalucia.es/services/DERA_g12_servicios/wfs';

  /** Capa única consumida por este geocoder. */
  public static readonly LAYER_ORG_HUMANITARIAS =
    'g12_36_OrganizacionesHumanitarias';

  /** Identificador completo de la fuente para `properties.source_layer`. */
  public static readonly SOURCE_LAYER_ID =
    'DERA_g12_servicios:g12_36_OrganizacionesHumanitarias';

  /**
   * Configuración por defecto.
   *
   * - `outputSRS = EPSG:25830` — fuente nativa, sin reproyección.
   * - `fuzzyThreshold = 0.40` — análogo a g12_37 PuestoSocorro porque
   *   g12_36 también expone `nombre` propio (texto descriptivo:
   *   «Cruz Roja Asamblea Local Baena», «Cáritas Diocesana de Jaén»)
   *   más discriminante que el match contra `localidad`+`municipio`
   *   exclusivo de g12_38 maremotos (que usa `0.45`).
   */
  protected getDefaultConfig(): SpecializedGeocoderConfig {
    return {
      wfsEndpoint: WFSOrganizacionesHumanitariasGeocoder.DERA_G12_ENDPOINT,
      layerName:
        WFSOrganizacionesHumanitariasGeocoder.LAYER_ORG_HUMANITARIAS,
      fuzzyThreshold: 0.40,
      timeout: 15000,
      outputSRS: 'EPSG:25830',
    };
  }

  /**
   * Parsea un feature GeoJSON aplicando HX-CAPTURA-MAXIMAL.
   *
   * Mantiene:
   * 1. Atributos fuente íntegros (`...props`).
   * 2. Campos provenance (capa, endpoint, schema, timestamp).
   * 3. Campos semánticos PTEL (tipología macro + tipo original + contexto).
   *
   * `MultiPoint` cardinalidad=1 verificada empíricamente en dataset
   * completo n=107 (2026-05-19): `parseGeometry` de la base normaliza
   * a `[x, y]` y se registra `geom_type` + `geom_pointCount` para
   * preservar fidelidad al schema fuente.
   *
   * A diferencia de g12_38 maremotos y g12_37 puestos socorro, g12_36
   * SÍ expone `direccion` (dirección postal completa) → se propaga al
   * campo `address` del `WFSFeature` resultante.
   */
  protected parseFeature(feature: GeoJSONFeature): WFSFeature | null {
    try {
      const props = feature.properties || {};
      const geom = feature.geometry;

      const pt = this.parseGeometry(
        geom,
        `OrganizacionHumanitaria feature ${String(feature.id ?? 'sin-id')}`,
      );
      if (!pt) {
        console.warn(
          'Feature OrganizacionHumanitaria sin geometría válida:',
          feature.id,
        );
        return null;
      }

      const [x, y] = pt;

      // Validación rango UTM30 Andalucía
      // (skill ptel-geocoding: X∈[100000,900000] Y∈[3900000,4400000])
      if (x < 100000 || x > 900000 || y < 3900000 || y > 4400000) {
        console.warn(
          'OrganizacionHumanitaria: coordenadas fuera de rango Andalucía:',
          x,
          y,
        );
        return null;
      }

      // Cálculo cardinalidad MultiPoint (preserva fidelidad al schema
      // fuente; dataset completo n=107 confirma cardinalidad=1 en el 100 %).
      const geomType = geom?.type ?? 'unknown';
      const geomPointCount =
        geom && geom.type === 'MultiPoint' && Array.isArray(geom.coordinates)
          ? geom.coordinates.length
          : 1;

      // Nombre canónico para el motor de fuzzy-matching:
      // nombre nativo → "tipo en municipio" → municipio → fallback.
      // g12_36 expone `nombre` descriptivo de la asamblea/sede
      // (ej. «Cruz Roja Asamblea Local Baena», «Cáritas Diocesana de Jaén»).
      const nombre = getStringProp(props, 'nombre');
      const tipo = getStringProp(props, 'tipo');
      const municipio = getStringProp(props, 'municipio');
      const direccion = getStringProp(props, 'direccion');
      const displayName =
        nombre ||
        (tipo && municipio ? `${tipo} en ${municipio}` : '') ||
        municipio ||
        `Organización humanitaria ${String(props.id_dera ?? feature.id ?? '?')}`;

      return {
        name: displayName,
        x,
        y,
        municipality: municipio,
        province: getStringProp(props, 'provincia'),
        // A diferencia de g12_37/g12_38, g12_36 SÍ expone `direccion`
        // postal completa → se propaga al campo `address` del resultado.
        address: direccion ?? '',
        properties: {
          // HX-CAPTURA-MAXIMAL · campos fuente íntegros
          ...props,

          // Campos derivados (geometría)
          geom_type: geomType,
          geom_pointCount: geomPointCount,

          // Campos semánticos PTEL (DOP-WAVE2-7=α · TipologiaPTEL macro cerrada)
          tipologiaPTEL: 'EMERGENCIAS',
          tipoOriginal: TIPO_ORIGINAL_ORGANIZACION_HUMANITARIA,
          decretoReferencia: '197/2024',
          contextoEmergencia: 'HUMANITARIA_GENERAL',

          // Campos provenance (3B · HX-CAPTURA-MAXIMAL pleno)
          source_layer: WFSOrganizacionesHumanitariasGeocoder.SOURCE_LAYER_ID,
          source_endpoint:
            WFSOrganizacionesHumanitariasGeocoder.DERA_G12_ENDPOINT,
          schema_version: SCHEMA_VERSION_G12_36,
          fetched_at: new Date().toISOString(),
        },
      };
    } catch (error) {
      console.error('Error parseando feature OrganizacionHumanitaria:', error);
      return null;
    }
  }

  /**
   * Construye filtro CQL específico para `g12_36`.
   *
   * Override completo (no invoca `super`) — el filtro base usa claves
   * mayúsculas legacy que GeoServer DERA G12 rechaza con HTTP 400
   * (case-sensitive en CQL property names, verif. WSSD 15-may).
   *
   * Orden de preferencia:
   * 1. `cod_mun = 'X'` (INE5 exacto) si se aporta `codMun`.
   * 2. `municipio ILIKE '%X%'` (texto libre) si solo hay `municipality`.
   * 3. `provincia ILIKE '%X%'` (filtro provincial adicional).
   * 4. `tipo ILIKE '%X%'` (filtro fino categórico Cruz roja\|Cáritas).
   * 5. `BBOX(geom, …, 'EPSG:25830')` (filtro espacial).
   */
  protected buildCQLFilter(
    options: OrganizacionesHumanitariasSearchOptions,
  ): string {
    const filters: string[] = [];

    if (options.codMun) {
      filters.push(`cod_mun='${this.escapeCQL(options.codMun)}'`);
    } else if (options.municipality) {
      filters.push(
        `municipio ILIKE '%${this.escapeCQL(options.municipality)}%'`,
      );
    }

    if (options.province) {
      filters.push(
        `provincia ILIKE '%${this.escapeCQL(options.province)}%'`,
      );
    }

    if (options.tipoOrganizacion) {
      filters.push(
        `tipo ILIKE '%${this.escapeCQL(options.tipoOrganizacion)}%'`,
      );
    }

    if (options.bbox) {
      const [minX, minY, maxX, maxY] = options.bbox;
      filters.push(
        `BBOX(geom,${minX},${minY},${maxX},${maxY},'EPSG:25830')`,
      );
    }

    return filters.length > 0 ? filters.join(' AND ') : '';
  }
}
